/**
 * GeneratorReport — /generator-report — Colton's standby-generator annual
 * maintenance report (BE-GEN-MNT-01), native and mobile-first.
 *
 * WHY NATIVE: Jobber can hold the checklist as a Job Form, but its answers can
 * never be read back out (API 2026-05-12 exposes ZERO query/output fields for form
 * data and no form webhook). So ElecBid owns the record and mirrors OUT one-way —
 * a formatted note plus the branded PDF land on the Jobber job — which is what
 * makes trending, wear flags, deficiency follow-ups and the customer PDF possible.
 *
 * The 228-item form is stepped through one section at a time (a single scroll of
 * 228 controls is unusable on a phone in a mechanical room). Three things keep it
 * from being a chore:
 *   • last year's answers are prefilled and last year's MEASUREMENTS sit beside
 *     this year's field, so wear is visible while the tech is still standing there
 *   • every keystroke lands in localStorage first, so a dead zone can't lose work
 *   • checks are tri-state (done / N/A / not yet) — a real unit has no radiator,
 *     no adjustable valves and no battery warmer, and a form that can't say so
 *     gets abandoned
 *
 * The schema is FETCHED from /api/generator-maintenance?action=schema rather than
 * imported: it lives in api/_generatorChecklist.js (CRA's module scope forbids
 * importing from outside src/), which guarantees the phone, the PDF and the
 * server's validation can never drift apart.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import { supabase } from "../lib/supabase";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1e2328", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4", faint: "#5a6374",
  red: "#e5484d", green: "#46a758", yellow: "#f5d90a", blue: "#3b82f6", orange: "#f59e0b",
};
const PHOTO_BUCKET = "assessment-videos";
const LS_KEY = (id) => `elecbid_genreport_${id}`;

async function api(action, body = {}) {
  let r;
  try {
    r = await apiFetch("/api/generator-maintenance", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...body }),
    });
  } catch {
    return { ok: false, error: "No connection — your work is saved on this phone and will sync when you have signal", offline: true };
  }
  return r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
}

async function downscalePhoto(file, maxDim = 1600, quality = 0.82) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1 && file.size < 1_200_000) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return blob || file;
  } catch {
    return file;
  }
}

function Btn({ children, kind, disabled, onClick, style, title }) {
  const base = {
    padding: "10px 15px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
    border: `1px solid ${kind === "pri" ? C.lime : kind === "danger" ? C.red : C.border}`,
    background: kind === "pri" ? C.lime : kind === "danger" ? "rgba(229,72,77,.12)" : C.card,
    color: kind === "pri" ? "#10130a" : kind === "danger" ? C.red : C.text,
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1,
  };
  return <button title={title} onClick={onClick} disabled={disabled} style={{ ...base, ...style }}>{children}</button>;
}

const SEV = { high: C.red, medium: C.orange, low: C.yellow };
function FlagList({ flags, compact }) {
  if (!flags?.length) return null;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {flags.map((f) => (
        <div key={f.id} style={{
          display: "flex", gap: 8, alignItems: "flex-start", padding: compact ? "6px 8px" : "9px 11px",
          background: "rgba(255,255,255,.02)", border: `1px solid ${SEV[f.severity] || C.border}`, borderRadius: 8,
        }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: SEV[f.severity] || C.muted, whiteSpace: "nowrap", paddingTop: 1 }}>
            {f.severity === "high" ? "PRIORITY" : f.severity === "medium" ? "ATTENTION" : "NOTE"}
          </span>
          <span style={{ fontSize: 12.5, lineHeight: 1.45, color: C.text }}>{f.message}</span>
        </div>
      ))}
    </div>
  );
}

// ── One checklist item ──────────────────────────────────────────────────────
function Item({ item, value, prior, onChange }) {
  const label = <span style={{ fontSize: 13, lineHeight: 1.4, color: C.text }}>{item.label}</span>;

  // Last year's answer, shown where it actually helps a decision.
  const priorHint = prior !== undefined && prior !== null && prior !== "" && item.t !== "check" && item.t !== "defect"
    ? <span style={{ fontSize: 11, color: C.faint, whiteSpace: "nowrap" }}>last: <b style={{ color: C.muted }}>{String(prior)}</b>{item.unit ? ` ${item.unit}` : ""}</span>
    : null;

  // Movement since last year, right where the number is entered.
  let delta = null;
  if (item.t === "num" && prior !== undefined && prior !== null && prior !== "" && value !== undefined && value !== null && value !== "") {
    const a = Number(prior), b = Number(value);
    if (Number.isFinite(a) && Number.isFinite(b) && a !== 0) {
      const pct = Math.round(((b - a) / Math.abs(a)) * 100);
      if (pct !== 0) {
        delta = <span style={{ fontSize: 11, fontWeight: 700, color: Math.abs(pct) >= 15 ? C.orange : C.faint, whiteSpace: "nowrap" }}>
          {pct > 0 ? "+" : ""}{pct}%
        </span>;
      }
    }
  }

  const inputStyle = {
    background: C.surface, color: C.text, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: "9px 10px", fontSize: 14, width: "100%", boxSizing: "border-box",
  };
  const row = { padding: "9px 0", borderBottom: `1px solid ${C.border}` };

  if (item.t === "caution") {
    return (
      <div style={{ ...row, display: "flex", gap: 8, alignItems: "flex-start" }}>
        <span style={{ color: C.yellow, fontSize: 13, fontWeight: 800, lineHeight: 1.3 }}>!</span>
        <span style={{ fontSize: 12.5, lineHeight: 1.45, color: C.muted, fontStyle: "italic" }}>{item.label}</span>
      </div>
    );
  }

  if (item.t === "check" || item.t === "defect") {
    const isDefect = item.t === "defect";
    const on = isDefect ? value === true : value === true;
    const na = value === "na";
    return (
      <div style={{ ...row, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <button
          onClick={() => onChange(on ? (isDefect ? false : false) : true)}
          aria-pressed={on}
          style={{
            flexShrink: 0, width: 26, height: 26, borderRadius: 6, marginTop: 1, cursor: "pointer",
            border: `1.5px solid ${on ? (isDefect ? C.orange : C.lime) : na ? C.faint : C.border}`,
            background: on ? (isDefect ? "rgba(245,158,11,.18)" : "rgba(165,206,58,.18)") : C.surface,
            color: on ? (isDefect ? C.orange : C.lime) : C.faint, fontSize: 15, fontWeight: 900, lineHeight: 1, padding: 0,
          }}
        >{on ? "✓" : na ? "–" : ""}</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {label}
          {item.critical && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: C.yellow }}>MUST CONFIRM</span>}
        </div>
        {!isDefect && (
          <button
            onClick={() => onChange(na ? false : "na")}
            style={{
              flexShrink: 0, padding: "3px 8px", borderRadius: 999, cursor: "pointer", fontSize: 10.5, fontWeight: 800,
              border: `1px solid ${na ? C.blue : C.border}`, background: na ? "rgba(59,130,246,.16)" : "transparent",
              color: na ? C.blue : C.faint,
            }}
          >N/A</button>
        )}
      </div>
    );
  }

  if (item.t === "note") {
    return (
      <div style={row}>
        <div style={{ marginBottom: 6 }}>{label}</div>
        <textarea
          value={value ?? ""} rows={3} onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </div>
    );
  }

  if (item.t === "choice") {
    return (
      <div style={row}>
        <div style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", gap: 10 }}>{label}{priorHint}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {item.options.map((opt) => {
            const on = value === opt;
            return (
              <button key={opt} onClick={() => onChange(on ? "" : opt)} style={{
                padding: "7px 12px", borderRadius: 999, cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                border: `1px solid ${on ? C.lime : C.border}`, background: on ? "rgba(165,206,58,.16)" : C.surface,
                color: on ? C.lime : C.muted,
              }}>{opt}</button>
            );
          })}
        </div>
      </div>
    );
  }

  // text / num / date / sig
  return (
    <div style={row}>
      <div style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          {label}
          {item.required && <span style={{ color: C.red, marginLeft: 4 }}>*</span>}
        </span>
        <span style={{ display: "flex", gap: 8, alignItems: "baseline" }}>{delta}{priorHint}</span>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type={item.t === "date" ? "date" : item.t === "num" ? "number" : "text"}
          inputMode={item.t === "num" ? "decimal" : undefined}
          step={item.t === "num" ? "any" : undefined}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={item.t === "sig" ? "Type your full name to sign" : ""}
          style={{ ...inputStyle, fontStyle: item.t === "sig" ? "italic" : "normal" }}
        />
        {item.unit && <span style={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{item.unit}</span>}
      </div>
    </div>
  );
}

// ── The report form ─────────────────────────────────────────────────────────
function ReportForm({ reportId, onExit }) {
  const [schema, setSchema] = useState(null);
  const [state, setState] = useState(null);      // { report, unit, prior, priorAnswers, trend, wearFlags, progress }
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [flags, setFlags] = useState([]);
  const [progress, setProgress] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | offline
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [gate, setGate] = useState(null);        // { missing[], warnings[] }
  const [result, setResult] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busyPhoto, setBusyPhoto] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const fileRef = useRef(null);

  // Autosave without the last-write-wins trap: the local answers object is the
  // only writer, saves are serialized (one in flight, a dirty flag re-runs after),
  // and a save RESPONSE never writes back into `answers`. Without that rule a slow
  // save's reply can resurrect the values it was carrying and clobber newer typing.
  const answersRef = useRef({});
  const inFlight = useRef(false);
  const dirty = useRef(false);
  const timer = useRef(null);

  useEffect(() => { answersRef.current = answers; }, [answers]);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([api("schema"), api("report", { reportId })]);
    if (!s.ok) { setErr(s.error || "Couldn't load the checklist"); return; }
    setSchema(s);
    if (!r.ok) { setErr(r.error || "Couldn't load this report"); return; }
    setState(r);
    setFlags(r.wearFlags || []);
    setProgress(r.progress || null);

    // A local draft newer than the server's copy wins: that's the dead-zone case.
    let start = r.report.answers || {};
    try {
      const raw = localStorage.getItem(LS_KEY(reportId));
      if (raw) {
        const local = JSON.parse(raw);
        const serverAt = new Date(r.report.updated_at || 0).getTime();
        if (local?.savedAt && local.savedAt > serverAt && local.answers) {
          start = { ...start, ...local.answers };
          setSaveState("offline");
        }
      }
    } catch { /* corrupt local draft is not worth failing over */ }
    setAnswers(start);
    setPhotos(r.report.photo_paths || []);
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const pushSave = useCallback(async () => {
    if (inFlight.current) { dirty.current = true; return; }
    inFlight.current = true;
    setSaveState("saving");
    const payload = answersRef.current;
    const r = await api("save-draft", { reportId, answers: payload });
    inFlight.current = false;
    if (r.ok) {
      setFlags(r.wearFlags || []);
      setProgress(r.progress || null);
      setSaveState("saved");
      try { localStorage.removeItem(LS_KEY(reportId)); } catch { /* private mode */ }
    } else {
      setSaveState(r.offline ? "offline" : "idle");
      if (!r.offline) setErr(r.error || "Couldn't save");
    }
    if (dirty.current) { dirty.current = false; pushSave(); }
  }, [reportId]);

  // Rescue for a draft with no job. The server fills in customer/address, but the
  // merge only fills BLANKS — anything already typed on this phone wins, same rule
  // as the autosave: a response must never overwrite live work.
  const linkToJob = async (jobNumber) => {
    setLinkBusy(true); setErr(null);
    const r = await api("link-job", { reportId, jobNumber });
    setLinkBusy(false);
    if (!r.ok) { setErr(r.error || "Couldn't link that job"); return; }
    setLinking(false);
    setState(r);
    setFlags(r.wearFlags || []);
    setProgress(r.progress || null);
    setAnswers((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(r.report.answers || {})) {
        if (next[k] === undefined || String(next[k] ?? "").trim() === "") next[k] = v;
      }
      answersRef.current = next;
      return next;
    });
  };

  const setValue = (id, v) => {
    setAnswers((prev) => {
      const next = { ...prev, [id]: v };
      answersRef.current = next;
      // Local first, always — the phone is the only storage guaranteed reachable.
      try { localStorage.setItem(LS_KEY(reportId), JSON.stringify({ answers: next, savedAt: Date.now() })); } catch { /* ignore */ }
      return next;
    });
    clearTimeout(timer.current);
    timer.current = setTimeout(pushSave, 1200);
  };

  const addPhotos = async (files) => {
    setBusyPhoto(true); setErr(null);
    try {
      const paths = [...photos];
      for (const f of Array.from(files || [])) {
        if (paths.length >= 20) break;
        const blob = await downscalePhoto(f);
        const path = `generator-reports/photos/${reportId}/${crypto.randomUUID()}.jpg`;
        const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, blob, { contentType: "image/jpeg", upsert: true });
        if (error) throw new Error(`Photo upload failed — ${error.message || "check your signal"}`);
        paths.push(path);
      }
      setPhotos(paths);
      await api("save-draft", { reportId, answers: answersRef.current, photoPaths: paths });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusyPhoto(false);
    }
  };

  const doSubmit = async (confirmWarnings) => {
    setSubmitting(true); setErr(null);
    clearTimeout(timer.current);
    await pushSave();
    const r = await api("submit", { reportId, answers: answersRef.current, confirmWarnings });
    setSubmitting(false);
    if (r.ok) {
      try { localStorage.removeItem(LS_KEY(reportId)); } catch { /* ignore */ }
      setGate(null);
      setResult(r);
      return;
    }
    if (r.error === "incomplete" || r.error === "needs_confirmation") {
      setGate({ kind: r.error, missing: r.missing || [], warnings: r.warnings || [] });
      return;
    }
    setErr(r.error || "Couldn't submit");
  };

  const openPdf = async () => {
    const r = await api("pdf", { reportId });
    if (r.ok && r.url) window.open(r.url, "_blank", "noopener");
    else setErr(r.error || "Couldn't build the PDF");
  };

  // Memoised so the per-section progress useMemo below isn't invalidated on every
  // render by a fresh [] identity.
  const sections = useMemo(() => schema?.sections || [], [schema]);
  const section = sections[step];
  const priorAnswers = state?.priorAnswers || {};

  const sectionDone = useMemo(() => {
    const out = {};
    for (const s of sections) {
      const counts = progress?.sections?.[s.key];
      out[s.key] = counts ? counts : null;
    }
    return out;
  }, [sections, progress]);

  if (err && !schema) return <Shell><Banner kind="err" text={err} /><Btn onClick={onExit}>Back</Btn></Shell>;
  if (!schema || !state) return <Shell><div style={{ color: C.muted, padding: 20 }}>Loading the checklist…</div></Shell>;

  // ── Submitted: the outcome, not the form ──────────────────────────────────
  if (result) {
    return (
      <Shell>
        <h2 style={{ margin: "0 0 4px", fontSize: 19, color: C.text }}>Report submitted</h2>
        <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
          {state.report.client_name || "This unit"} · {answers["eq.date"] || state.report.service_date}
        </div>

        {result.wearFlags?.length > 0 && (
          <Card title={`Condition flags (${result.wearFlags.length})`}>
            <FlagList flags={result.wearFlags} />
          </Card>
        )}

        <Card title="What happened next">
          <Row label="Branded PDF" value={result.pdf?.ok ? "Built" : `Failed — ${result.pdf?.error || "will retry"}`} good={result.pdf?.ok} />
          <Row
            label="Jobber note + PDF"
            value={result.jobber?.ok ? `Posted to the job${result.jobber.attached ? ` with ${result.jobber.attached} attachment${result.jobber.attached === 1 ? "" : "s"}` : ""}`
              : result.jobber?.skipped === "no_jobber_job" ? "No Jobber job linked — nothing to post to"
              : `Queued — retries automatically${result.jobber?.error ? ` (${result.jobber.error})` : ""}`}
            good={result.jobber?.ok}
          />
          <Row
            label="Follow-up work request"
            value={result.followup?.ok && result.followup.requestId ? `Created in Jobber for ${result.defects?.length || 0} deficienc${(result.defects?.length || 0) === 1 ? "y" : "ies"}`
              : result.followup?.skipped === "no_defects" ? "Not needed — no deficiencies marked"
              : `Queued — retries automatically${result.followup?.error ? ` (${result.followup.error})` : ""}`}
            good={result.followup?.ok}
          />
          {result.nextServiceDue && <Row label="Next service due" value={result.nextServiceDue} good />}
        </Card>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
          <Btn kind="pri" onClick={openPdf}>Open the PDF</Btn>
          <Btn onClick={onExit}>Done</Btn>
        </div>
        {err && <div style={{ marginTop: 10 }}><Banner kind="err" text={err} /></div>}
      </Shell>
    );
  }

  const pct = progress?.total ? Math.round((progress.answered / progress.total) * 100) : 0;

  return (
    <Shell>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 4 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>
            {state.report.client_name || "Generator service"}
          </div>
          <div style={{ fontSize: 11.5, color: C.faint }}>
            {schema.doc.code} · {schema.doc.rev}
            {state.report.job_number ? ` · job #${state.report.job_number}` : ""}
            {state.prior?.serviceDate ? ` · last serviced ${state.prior.serviceDate}` : " · first service on file"}
          </div>
        </div>
        <SaveChip stateName={saveState} />
      </div>

      {/* Not linked to a job — nothing will reach Jobber until it is. */}
      {!state.report.job_number && (
        <div style={{ marginTop: 10, padding: "10px 12px", background: "rgba(245,158,11,.10)", border: `1px solid ${C.orange}`, borderRadius: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: C.orange }}>Not linked to a Jobber job</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
            You can fill this out and it saves fine, but on submit there's no job to post the report and PDF
            to, and a ticked deficiency won't raise a work request. Link it any time before you submit.
          </div>
          <div style={{ marginTop: 8 }}>
            <Btn kind="pri" disabled={linkBusy} onClick={() => setLinking(true)} style={{ padding: "6px 11px", fontSize: 12 }}>
              {linkBusy ? "Linking…" : "Link to a job"}
            </Btn>
          </div>
        </div>
      )}

      {linking && (
        <JobPicker
          heading="Link this report to a job"
          initialQ={state.report.client_name || state.unit?.client_name || ""}
          busy={linkBusy}
          onClose={() => setLinking(false)}
          onPick={linkToJob}
        />
      )}

      {/* Progress */}
      <div style={{ margin: "10px 0 12px" }}>
        <div style={{ height: 5, background: C.card, borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: C.lime, transition: "width .2s" }} />
        </div>
        <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>
          {progress?.answered || 0} of {progress?.total || 0} answered
        </div>
      </div>

      {/* Section chips */}
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, marginBottom: 4 }}>
        {sections.map((s, i) => {
          const c = sectionDone[s.key];
          const complete = c && c.total > 0 && c.answered >= c.total;
          const on = i === step;
          return (
            <button key={s.key} onClick={() => { setStep(i); window.scrollTo({ top: 0 }); }} style={{
              flexShrink: 0, padding: "6px 11px", borderRadius: 999, cursor: "pointer", fontSize: 11.5, fontWeight: 700,
              border: `1px solid ${on ? C.lime : complete ? C.green : C.border}`,
              background: on ? "rgba(165,206,58,.16)" : C.card,
              color: on ? C.lime : complete ? C.green : C.muted, whiteSpace: "nowrap",
            }}>
              {i === 0 ? "Equipment" : s.key === "signoff" ? "Sign-off" : `${i}. ${s.title.split(/[ -]/)[0]}`}
              {complete ? " ✓" : c && c.total ? ` ${c.answered}/${c.total}` : ""}
            </button>
          );
        })}
      </div>

      {err && <div style={{ marginBottom: 10 }}><Banner kind="err" text={err} onClose={() => setErr(null)} /></div>}

      {/* Live wear flags — visible while the tech is still at the unit */}
      {flags.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, marginBottom: 6, letterSpacing: .4 }}>
            FROM YOUR MEASUREMENTS ({flags.length})
          </div>
          <FlagList flags={flags} compact />
        </div>
      )}

      {/* The section */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px" }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: C.lime, marginBottom: 2 }}>
          {step === 0 || section.key === "signoff" ? section.title : `${step}. ${section.title}`}
        </div>
        {step === 0 && <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5, marginBottom: 6 }}>{schema.doc.intervalNote}</div>}
        {section.items.map((item) => (
          <Item
            key={item.id}
            item={item}
            value={answers[item.id]}
            prior={priorAnswers[item.id]}
            onChange={(v) => setValue(item.id, v)}
          />
        ))}

        {/* Photos ride with the last section so they end up on the Jobber note */}
        {section.key === "signoff" && (
          <div style={{ paddingTop: 12 }}>
            <div style={{ fontSize: 13, color: C.text, marginBottom: 6 }}>Photos (attached to the Jobber note)</div>
            {photos.length > 0 && (
              <div style={{ fontSize: 12, color: C.green, marginBottom: 6 }}>{photos.length} photo{photos.length === 1 ? "" : "s"} uploaded</div>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple capture="environment"
              onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} style={{ display: "none" }} />
            <Btn onClick={() => fileRef.current?.click()} disabled={busyPhoto || photos.length >= 20}>
              {busyPhoto ? "Uploading…" : photos.length >= 20 ? "20 photo limit reached" : "Add photos"}
            </Btn>
          </div>
        )}
      </div>

      {/* Stepper */}
      <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
        <Btn disabled={step === 0} onClick={() => { setStep(step - 1); window.scrollTo({ top: 0 }); }}>Back</Btn>
        {step < sections.length - 1
          ? <Btn kind="pri" onClick={() => { pushSave(); setStep(step + 1); window.scrollTo({ top: 0 }); }}>Next</Btn>
          : <Btn kind="pri" disabled={submitting} onClick={() => doSubmit(false)}>{submitting ? "Submitting…" : "Submit report"}</Btn>}
        <div style={{ flex: 1 }} />
        <Btn onClick={onExit}>Save &amp; close</Btn>
      </div>

      {/* Submit gate */}
      {gate && (
        <div style={{ marginTop: 14, background: C.card, border: `1px solid ${gate.kind === "incomplete" ? C.red : C.yellow}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: gate.kind === "incomplete" ? C.red : C.yellow, marginBottom: 8 }}>
            {gate.kind === "incomplete" ? "Can't submit yet" : "Before you submit"}
          </div>
          {gate.missing.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>These are required:</div>
              <ul style={{ margin: "0 0 10px", paddingLeft: 18, color: C.text, fontSize: 12.5, lineHeight: 1.6 }}>
                {gate.missing.map((m) => {
                  const i = sections.findIndex((s) => s.key === m.section);
                  return (
                    <li key={m.id}>
                      {m.label}{" "}
                      {i >= 0 && <button onClick={() => { setGate(null); setStep(i); window.scrollTo({ top: 0 }); }}
                        style={{ background: "none", border: "none", color: C.lime, cursor: "pointer", fontSize: 12, textDecoration: "underline", padding: 0 }}>go there</button>}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {gate.warnings.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Worth a second look:</div>
              <ul style={{ margin: "0 0 10px", paddingLeft: 18, color: C.text, fontSize: 12.5, lineHeight: 1.6 }}>
                {gate.warnings.map((w) => <li key={w.id}>{w.label}</li>)}
              </ul>
            </>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {gate.kind === "needs_confirmation" && (
              <Btn kind="pri" disabled={submitting} onClick={() => doSubmit(true)}>
                {submitting ? "Submitting…" : "Submit anyway"}
              </Btn>
            )}
            <Btn onClick={() => setGate(null)}>Keep editing</Btn>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ── Job picker ──────────────────────────────────────────────────────────────
// A report has to land on a job. Without one the submit still saves and still
// builds the PDF, but there's no Jobber job to post the note to and the follow-up
// request has nothing to hang off — the service record just sits in ElecBid
// attached to nothing. So picking the job is the FIRST step, not an afterthought,
// and this same sheet is the rescue for a draft that never got one.
function JobPicker({ heading, initialQ, onPick, onSkip, onClose, busy }) {
  const [q, setQ] = useState(initialQ || "");
  const [jobs, setJobs] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const r = await api("jobs", { q: q.trim() || undefined });
      if (!r.ok) { setErr(r.error || "Couldn't load jobs"); setJobs([]); return; }
      setErr(null); setJobs(r.jobs || []);
    }, 280);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.62)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 620, maxHeight: "88vh", display: "flex", flexDirection: "column", background: C.bg, border: `1px solid ${C.border}`, borderRadius: "14px 14px 0 0", padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 3 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{heading || "Which job?"}</div>
          <Btn onClick={onClose} style={{ padding: "5px 10px", fontSize: 12 }}>Cancel</Btn>
        </div>
        <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 10 }}>
          The report posts its note and any follow-up to this job.
        </div>

        <input
          value={q} onChange={(e) => setQ(e.target.value)} autoFocus
          placeholder="Search client, job title, or job number…"
          style={{ width: "100%", boxSizing: "border-box", background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 9, padding: "10px 12px", fontSize: 14, marginBottom: 10 }}
        />

        {err && <div style={{ marginBottom: 8 }}><Banner kind="err" text={err} /></div>}

        <div style={{ overflowY: "auto", display: "grid", gap: 7, flex: 1, minHeight: 60 }}>
          {jobs === null ? <div style={{ color: C.muted, fontSize: 13, padding: 8 }}>Loading jobs…</div>
            : !jobs.length ? <div style={{ color: C.muted, fontSize: 13, padding: 8 }}>No jobs match “{q}”.</div>
            : jobs.map((j) => (
              <button key={j.jobNumber} disabled={busy} onClick={() => onPick(j.jobNumber)} style={{
                textAlign: "left", cursor: busy ? "default" : "pointer", padding: "9px 11px", background: C.card,
                border: `1px solid ${j.generator ? C.lime : C.border}`, borderRadius: 9, opacity: busy ? 0.6 : 1,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {j.clientName || "(no client)"}
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: C.faint, whiteSpace: "nowrap" }}>#{j.jobNumber}</span>
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {j.title || "(no title)"}
                </div>
                {j.hint && <div style={{ fontSize: 10.5, fontWeight: 700, color: C.lime, marginTop: 2 }}>{j.hint}</div>}
              </button>
            ))}
        </div>

        {onSkip && (
          <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 10, paddingTop: 10 }}>
            <button onClick={onSkip} disabled={busy} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
              <div style={{ fontSize: 12.5, color: C.yellow, fontWeight: 700 }}>No job for this yet — start anyway</div>
              <div style={{ fontSize: 11, color: C.faint, marginTop: 1 }}>
                Nothing will post to Jobber until you link a job. You can do that from the report at any time before submitting.
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Index: equipment + recent reports + start ───────────────────────────────
function Index({ onOpen }) {
  const [units, setUnits] = useState(null);
  const [reports, setReports] = useState([]);
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const [u, r] = await Promise.all([api("units", {}), api("my-reports", { limit: 25 })]);
    if (!u.ok) setErr(u.error || "Couldn't load the equipment list");
    setUnits(u.units || []);
    setReports(r.reports || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // The job is chosen BEFORE the report exists, so a report is bound from birth.
  // `pick` holds what we're picking a job for: {} = a brand-new unit, {unitId} =
  // servicing one already on file (its client seeds the search).
  const [pick, setPick] = useState(null);
  const [chooseUnits, setChooseUnits] = useState(null);
  const [pendingJob, setPendingJob] = useState(null);

  const startFor = async (unitId, jobNumber) => {
    setStarting(true); setErr(null);
    const body = {};
    if (unitId) body.unitId = unitId;
    if (jobNumber) body.jobNumber = jobNumber;
    const r = await api("start", body);
    setStarting(false);
    if (r.ok) { onOpen(r.report.id); return; }
    // The job's client has more than one generator — ask which before opening.
    if (r.error === "choose_unit") {
      setPick(null); setPendingJob(jobNumber || null); setChooseUnits(r.units || []);
      return;
    }
    setErr(r.error || "Couldn't start a report");
  };

  const term = q.trim().toLowerCase();
  const shown = (units || []).filter((u) => !term ||
    [u.client_name, u.property_address, u.manufacturer, u.model, u.serial_number]
      .some((f) => String(f || "").toLowerCase().includes(term)));

  const dueColor = { overdue: C.red, due_soon: C.yellow, ok: C.green, unknown: C.faint };
  const dueLabel = { overdue: "OVERDUE", due_soon: "Due soon", ok: "Up to date", unknown: "Never serviced" };

  return (
    <Shell>
      <h2 style={{ margin: "0 0 2px", fontSize: 19, color: C.text }}>Generator maintenance</h2>
      <div style={{ fontSize: 12, color: C.faint, marginBottom: 14 }}>
        Standby generator annual reports · BE-GEN-MNT-01 · annual or every 200 hours, whichever comes first
      </div>

      {err && <div style={{ marginBottom: 10 }}><Banner kind="err" text={err} onClose={() => setErr(null)} /></div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <Btn kind="pri" disabled={starting} onClick={() => setPick({})}>
          {starting ? "Starting…" : "New report (new unit)"}
        </Btn>
      </div>

      {chooseUnits && (
        <Card title="Which generator?">
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 9 }}>
            This client has more than one unit on file{pendingJob ? ` (job #${pendingJob})` : ""}.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {chooseUnits.map((u) => (
              <button key={u.id} disabled={starting} onClick={() => startFor(u.id, pendingJob)} style={{ textAlign: "left", cursor: "pointer", padding: "9px 11px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 9 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>
                  {[u.manufacturer, u.model, u.size_kw ? `${u.size_kw}kW` : null].filter(Boolean).join(" ") || "Generator"}
                </div>
                <div style={{ fontSize: 11.5, color: C.faint }}>
                  {u.serial_number ? `S/N ${u.serial_number}` : "no serial recorded"}
                  {u.next_service_due ? ` · due ${u.next_service_due}` : ""}
                </div>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 9 }}>
            <Btn onClick={() => { setChooseUnits(null); setPendingJob(null); }} style={{ padding: "6px 10px", fontSize: 12 }}>Cancel</Btn>
          </div>
        </Card>
      )}

      <Card title={`Equipment on file${units ? ` (${units.length})` : ""}`}>
        {units === null ? <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
          : !units.length ? <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.5 }}>
              No generators on file yet. Start a report from a job on your schedule, or hit “New report” — the
              nameplate you enter in section 0 becomes the equipment record, so next year everything is prefilled.
            </div>
          : (
            <>
              {units.length > 6 && (
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, address, model, serial…"
                  style={{ width: "100%", boxSizing: "border-box", background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, marginBottom: 10 }} />
              )}
              <div style={{ display: "grid", gap: 8 }}>
                {shown.map((u) => (
                  <div key={u.id} style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", padding: "9px 10px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {u.client_name || "(no client name)"}
                      </div>
                      <div style={{ fontSize: 11.5, color: C.faint }}>
                        {[u.manufacturer, u.model, u.size_kw ? `${u.size_kw}kW` : null].filter(Boolean).join(" ") || "unit details not recorded"}
                        {u.serial_number ? ` · S/N ${u.serial_number}` : ""}
                      </div>
                      <div style={{ fontSize: 11, color: dueColor[u.dueStatus], fontWeight: 700, marginTop: 2 }}>
                        {dueLabel[u.dueStatus]}{u.next_service_due ? ` · due ${u.next_service_due}` : ""}
                      </div>
                    </div>
                    <Btn onClick={() => setPick({ unitId: u.id, clientName: u.client_name })} disabled={starting} style={{ flexShrink: 0, padding: "7px 11px", fontSize: 12 }}>Service</Btn>
                  </div>
                ))}
              </div>
            </>
          )}
      </Card>

      <Card title={`Recent reports${reports.length ? ` (${reports.length})` : ""}`}>
        {!reports.length ? <div style={{ color: C.muted, fontSize: 13 }}>Nothing yet.</div> : (
          <div style={{ display: "grid", gap: 8 }}>
            {reports.map((r) => {
              const high = (r.wear_flags || []).filter((f) => f.severity === "high").length;
              return (
                <button key={r.id} onClick={() => onOpen(r.id)} style={{ textAlign: "left", cursor: "pointer", padding: "9px 10px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{r.client_name || "(no client)"}</span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: r.status === "draft" ? C.yellow : C.green, whiteSpace: "nowrap" }}>
                      {r.status === "draft" ? "DRAFT" : "SUBMITTED"}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: C.faint, marginTop: 2 }}>
                    {r.service_date || "no date"}{r.job_number ? ` · job #${r.job_number}` : ""}{r.tech_name ? ` · ${r.tech_name}` : ""}
                    {r.final_status ? ` · ${r.final_status}` : ""}
                  </div>
                  {(high > 0 || (r.defects || []).length > 0) && (
                    <div style={{ fontSize: 11, color: high ? C.red : C.orange, fontWeight: 700, marginTop: 2 }}>
                      {high ? `${high} priority flag${high === 1 ? "" : "s"}` : ""}
                      {high && (r.defects || []).length ? " · " : ""}
                      {(r.defects || []).length ? `${r.defects.length} deficienc${r.defects.length === 1 ? "y" : "ies"}` : ""}
                      {r.followup_request_id ? " · follow-up created" : ""}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <div style={{ marginTop: 16, fontSize: 11.5, color: C.faint, lineHeight: 1.6 }}>
        Submitting posts a summary and the branded PDF onto the Jobber job, and turns any ticked
        deficiency into a Jobber work request for the office to quote.{" "}
        <Link to="/schedule" style={{ color: C.lime }}>My Schedule</Link>
      </div>

      {pick && (
        <JobPicker
          heading={pick.unitId ? "Which job is this service on?" : "Which job is this report for?"}
          initialQ={pick.clientName || ""}
          busy={starting}
          onClose={() => setPick(null)}
          onPick={(jobNumber) => { setPick(null); startFor(pick.unitId || null, jobNumber); }}
          onSkip={() => { setPick(null); startFor(pick.unitId || null, null); }}
        />
      )}
    </Shell>
  );
}

// ── Chrome ──────────────────────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "16px 14px 90px", color: C.text }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>{children}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: .4, marginBottom: 9 }}>{title.toUpperCase()}</div>
      {children}
    </div>
  );
}
function Row({ label, value, good }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12.5, color: C.muted }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: good ? C.green : C.yellow, textAlign: "right" }}>{value}</span>
    </div>
  );
}
function Banner({ kind, text, onClose }) {
  const col = kind === "err" ? C.red : C.blue;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(229,72,77,.08)", border: `1px solid ${col}`, borderRadius: 8, padding: "9px 11px" }}>
      <span style={{ flex: 1, fontSize: 12.5, color: C.text, lineHeight: 1.45 }}>{text}</span>
      {onClose && <button onClick={onClose} style={{ background: "none", border: "none", color: col, cursor: "pointer", fontSize: 15, fontWeight: 800, padding: 0, lineHeight: 1 }}>×</button>}
    </div>
  );
}
function SaveChip({ stateName }) {
  const map = {
    idle: { t: "", c: C.faint },
    saving: { t: "Saving…", c: C.blue },
    saved: { t: "Saved ✓", c: C.green },
    offline: { t: "Saved on this phone — will sync", c: C.yellow },
  };
  const m = map[stateName] || map.idle;
  if (!m.t) return null;
  return <span style={{ fontSize: 10.5, fontWeight: 700, color: m.c, border: `1px solid ${m.c}`, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>{m.t}</span>;
}

// ── Route entry ─────────────────────────────────────────────────────────────
export default function GeneratorReport() {
  const { reportId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [autoErr, setAutoErr] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [choose, setChoose] = useState(null); // more than one unit on the client
  const tried = useRef(false);

  // Deep link from a Jobber visit's instructions or the /schedule button:
  // /generator-report?job=2509&visit=… → find-or-create the draft and go.
  const job = search.get("job");
  const visit = search.get("visit");
  useEffect(() => {
    if (reportId || !job || tried.current) return;
    tried.current = true;
    setAutoBusy(true);
    (async () => {
      const r = await api("start", { jobNumber: job, visitId: visit || undefined });
      setAutoBusy(false);
      if (r.ok) navigate(`/generator-report/${r.report.id}`, { replace: true });
      else if (r.error === "choose_unit") setChoose(r.units || []);
      else setAutoErr(r.error || "Couldn't open a report for that job");
    })();
  }, [reportId, job, visit, navigate]);

  const startForUnit = async (unitId) => {
    setAutoBusy(true);
    const r = await api("start", { unitId, jobNumber: job || undefined, visitId: visit || undefined });
    setAutoBusy(false);
    if (r.ok) navigate(`/generator-report/${r.report.id}`, { replace: true });
    else setAutoErr(r.error || "Couldn't open that unit");
  };

  if (reportId) return <ReportForm reportId={reportId} onExit={() => navigate("/generator-report")} />;

  if (choose) {
    return (
      <Shell>
        <h2 style={{ margin: "0 0 4px", fontSize: 18, color: C.text }}>Which generator?</h2>
        <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 14 }}>This client has more than one unit on file.</div>
        <div style={{ display: "grid", gap: 8 }}>
          {choose.map((u) => (
            <button key={u.id} onClick={() => startForUnit(u.id)} disabled={autoBusy} style={{ textAlign: "left", cursor: "pointer", padding: "10px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                {[u.manufacturer, u.model, u.size_kw ? `${u.size_kw}kW` : null].filter(Boolean).join(" ") || "Generator"}
              </div>
              <div style={{ fontSize: 11.5, color: C.faint }}>
                {u.serial_number ? `S/N ${u.serial_number}` : "no serial recorded"}
                {u.property_address ? ` · ${u.property_address}` : ""}
                {u.next_service_due ? ` · due ${u.next_service_due}` : ""}
              </div>
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  if (autoBusy) return <Shell><div style={{ color: C.muted, padding: 20 }}>Opening the report…</div></Shell>;
  if (autoErr) return <Shell><Banner kind="err" text={autoErr} /><div style={{ marginTop: 12 }}><Btn onClick={() => { setAutoErr(null); navigate("/generator-report", { replace: true }); }}>Equipment list</Btn></div></Shell>;

  return <Index onOpen={(id) => navigate(`/generator-report/${id}`)} />;
}
